import { useEffect, useState } from 'react';
import { integrations, type CloudflareIntegration, ApiError } from '@/lib/api';
import { ConfirmDialog } from './ConfirmDialog';

/**
 * Inline "connect Cloudflare" card. Shows the right state based on whether
 * the org has already connected:
 *   - Not connected → "Connect" CTA → opens token entry
 *   - Token entry  → instructions + deep link to CF dashboard + paste field
 *   - Connected    → "Connected as <zone>" + disconnect link
 */
const CF_TOKEN_HELP_URL = 'https://dash.cloudflare.com/profile/api-tokens';

export function CloudflareConnect({
  onChanged,
  detected,
}: {
  onChanged?: (state: CloudflareIntegration) => void;
  /** When true, this domain's NS resolves to Cloudflare — show a detection badge. */
  detected?: boolean;
}) {
  const [state, setState] = useState<CloudflareIntegration | null>(null);
  const [entering, setEntering] = useState(false);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  useEffect(() => {
    integrations.cloudflare
      .status()
      .then((s) => {
        setState(s);
        onChanged?.(s);
      })
      .catch(() => setState({ connected: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onConnect() {
    setBusy(true);
    setErr(null);
    try {
      const res = await integrations.cloudflare.connect(token.trim());
      setState(res);
      setEntering(false);
      setToken('');
      onChanged?.(res);
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? errorMessageFor(e.code)
          : 'Could not connect Cloudflare. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmDisconnect() {
    await integrations.cloudflare.disconnect();
    const next: CloudflareIntegration = { connected: false };
    setState(next);
    onChanged?.(next);
  }

  if (!state) return null;

  if (state.connected) {
    return (
      <>
        <div className="rounded-xl border border-[var(--color-border)] bg-[color-mix(in_oklab,#f48120_10%,transparent)] p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CloudflareLogo />
              <div>
                <div className="text-sm font-medium">Cloudflare connected</div>
                <div className="text-xs text-[var(--color-muted-foreground)]">
                  {state.zones?.length || 0} zone{state.zones?.length === 1 ? '' : 's'} available
                  {state.accountLabel ? ` · ${state.accountLabel}` : ''}
                </div>
              </div>
            </div>
            <button
              onClick={() => setDisconnectOpen(true)}
              disabled={busy}
              className="text-xs text-[var(--color-muted-foreground)] hover:underline"
            >
              Disconnect
            </button>
          </div>
        </div>
        <ConfirmDialog
          open={disconnectOpen}
          onClose={() => setDisconnectOpen(false)}
          onConfirm={onConfirmDisconnect}
          title="Disconnect Cloudflare?"
          description="Future custom-domain setups will go back to manual copy-paste DNS records. Your stored API token is deleted (we don't keep it after disconnect)."
          confirmLabel="Disconnect"
        />
      </>
    );
  }

  if (!entering) {
    return (
      <div
        className={`rounded-xl border p-4 ${
          detected
            ? 'border-[#16a34a] bg-[color-mix(in_oklab,#16a34a_8%,transparent)]'
            : 'border-dashed border-[var(--color-border)] bg-[var(--color-muted)]/30'
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <CloudflareLogo />
            <div>
              <div className="flex items-center gap-2 text-sm font-medium">
                {detected ? 'Cloudflare DNS detected' : 'Using Cloudflare?'}
                {detected && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#16a34a] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                    ✓ on Cloudflare
                  </span>
                )}
              </div>
              <div className="text-xs text-[var(--color-muted-foreground)]">
                {detected
                  ? 'We can write the required DNS records directly into your zone — one click instead of manual copy-paste.'
                  : "Skip the manual DNS step — we'll add records for you with one click."}
              </div>
            </div>
          </div>
          <button
            onClick={() => setEntering(true)}
            className="rounded-full bg-[var(--color-foreground)] px-4 py-1.5 text-xs font-medium text-[var(--color-background)] transition hover:opacity-90"
          >
            Connect Cloudflare
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CloudflareLogo />
          <h3 className="text-sm font-semibold">Connect your Cloudflare account</h3>
        </div>
        <button
          onClick={() => setEntering(false)}
          className="text-xs text-[var(--color-muted-foreground)] hover:underline"
        >
          Cancel
        </button>
      </div>

      <ol className="mt-4 space-y-2 text-sm text-[var(--color-muted-foreground)]">
        <li>
          1. Open{' '}
          <a
            href={CF_TOKEN_HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--color-primary)] underline"
          >
            Cloudflare → My Profile → API Tokens
          </a>{' '}
          and click <strong>Create Token</strong> → <strong>Custom token</strong>.
        </li>
        <li>
          2. Give it these permissions (and ideally scope to a specific zone for tighter security):
          <ul className="ml-5 mt-1 list-disc text-xs">
            <li>
              <code>Zone</code> → <code>Zone</code> → <code>Read</code>
            </li>
            <li>
              <code>Zone</code> → <code>DNS</code> → <code>Edit</code>
            </li>
          </ul>
        </li>
        <li>
          3. Click <strong>Continue to summary</strong> → <strong>Create Token</strong> → copy
          the token. Paste it below.
        </li>
      </ol>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          API Token
        </span>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Paste your Cloudflare API token"
          autoComplete="off"
          spellCheck={false}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 font-mono text-sm outline-none transition focus:border-[var(--color-ring)]"
        />
      </label>

      {err && <p className="mt-2 text-sm text-[var(--color-destructive)]">{err}</p>}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={onConnect}
          disabled={busy || token.length < 20}
          className="rounded-full bg-[var(--color-primary)] px-5 py-2 text-sm font-medium text-[var(--color-primary-foreground)] transition hover:brightness-110 disabled:opacity-60"
        >
          {busy ? 'Connecting…' : 'Connect'}
        </button>
        <span className="text-xs text-[var(--color-muted-foreground)]">
          Stored encrypted at rest. Disconnect anytime.
        </span>
      </div>
    </div>
  );
}

function CloudflareLogo() {
  return (
    <span
      aria-hidden
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f48120] text-white"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16.5 13.2c-.2-.6-.7-1-1.3-1.1l-7.8-.1c-.1 0-.2-.1-.2-.2v-.1c0-.2.1-.3.3-.4l7.9-.1c.9 0 1.9-.7 2.3-1.6l.5-1.3c0-.1 0-.1 0-.2-.5-2.3-2.5-4-5-4-2.3 0-4.2 1.4-5 3.4-.5-.4-1.1-.6-1.8-.5-1.2.1-2.2 1.1-2.3 2.3 0 .3 0 .6.1.9-2 .1-3.5 1.7-3.5 3.7 0 .2 0 .4.1.5 0 .1.1.2.2.2H16c.1 0 .2-.1.2-.2l.3-.9c.1-.4.1-.7 0-1.1z" />
      </svg>
    </span>
  );
}

function errorMessageFor(code: string): string {
  switch (code) {
    case 'token_invalid_format':
      return 'That doesn\'t look like a valid Cloudflare API token.';
    case 'token_inactive_or_invalid':
      return 'Cloudflare rejected the token — it\'s expired, disabled, or wrong.';
    case 'token_missing_zone_read':
      return 'Token is missing Zone:Read permission. Recreate with both Zone:Read and DNS:Edit.';
    default:
      return code;
  }
}
